/**
 * SEEING THE BOARD AS SOMEBODY ELSE WOULD.
 *
 * WHY IT EXISTS. "Is this button hidden because of their role, because of the
 * board, or because it is broken?" has cost more time during this build than
 * any other question. There are now three things that can hide a control — a
 * workspace role, a role on this particular board, and whether the board
 * admits them at all — and the only way to check was to sign in as somebody
 * else, which needs their sign-in link.
 *
 * WHAT IT IS, AND THE ONE THING IT IS NOT.
 *
 * It changes what the SCREEN offers, and nothing else. It does not change who
 * you are to the database: every read and every write still goes out as you,
 * still carries your identity, and is still judged by firestore.rules against
 * you. Nobody gains a single right by pretending to be somebody.
 *
 * THAT IS A LIMITATION AND IT IS ALSO THE POINT. A preview that really acted
 * as another person would be an impersonation feature — the most dangerous
 * thing in any access system, and the first thing a penetration test goes
 * looking for. This cannot be that, because the permission check does not live
 * here. What it shows is whether the INTERFACE agrees with the rules, which is
 * exactly the class of fault it was built to find: a button offered and then
 * refused, or a screen hidden from somebody entitled to it.
 *
 * So the honest description, and the words on the banner: this is what they
 * would SEE.
 */
import type { Role } from './permissions';

export interface Viewing {
  /** Whose eyes. Empty means your own. */
  email: string;
  /** The role they hold on the board being looked at, worked out as they are. */
  role: Role;
}

/**
 * WHOSE ROLE THE SCREEN SHOULD USE.
 *
 * Yours, unless a preview is running. Kept as one function because the answer
 * is needed in every component that hides anything, and "sometimes mine and
 * sometimes theirs" spread across thirty call sites is how a preview leaks
 * into a real permission decision.
 */
export function shownRole(mine: Role, viewing: Viewing | null): Role {
  return viewing ? viewing.role : mine;
}

/**
 * WHETHER A PREVIEW MAY BE STARTED AT ALL.
 *
 * Only a workspace admin, and never of an admin. Not because looking is
 * dangerous — it grants nothing — but because a preview of somebody who can
 * see everything shows nothing worth knowing, and offering it implies a power
 * it does not have.
 */
export function canPreview(myRole: Role, theirRole: Role): boolean {
  return myRole === 'admin' && theirRole !== 'admin';
}

/** What the banner says, which has to be unambiguous at a glance. */
export function previewBanner(name: string, boardName: string, role: Role): string {
  return `Seeing ${boardName} as ${name} would — ${role}. Your own access is unchanged.`;
}

/**
 * The one warning worth giving, and only where it is true.
 *
 * A preview of somebody who cannot open this board at all shows an empty
 * board, which looks exactly like a fault. Saying so first is the difference
 * between "this is broken" and "that is the answer".
 */
export function previewNote(canOpenBoard: boolean, name: string, boardName: string): string {
  return canOpenBoard
    ? ''
    : `${name} cannot open ${boardName} at all, so this shows nothing. That is the correct answer, not a fault.`;
}
